import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Select,
  Tag,
  Td,
  Text,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FaCopy, FaDownload, FaLinkedin } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  getMission,
  sendMission,
  closeMission,
  getMissionApplications,
  getMissionSubmissions,
  getMissionDeliveryStats,
  getMissionLink,
  createConsultantFromApplication,
  listPools,
  addToPoolFromApplication,
  downloadApplicationCv,
  downloadSubmissionCv,
} from "../../../api/partnersApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  PARTNERS_MISSION_STATUS_COLORS,
  PARTNERS_MISSION_STATUS_LABELS,
  PARTNERS_MISSION_STATUS_LABEL_KEYS,
  PARTNERS_SUBMISSION_STATUS_COLORS,
  PARTNERS_SUBMISSION_STATUS_LABELS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import {
  DataTable,
  StatusBadge,
  EmptyState,
  MiniIconButton,
} from "../../../components/Portal/ui";
import MissionFormModal from "./MissionFormModal";
import SubmissionDetailModal from "./SubmissionDetailModal";
import { formatCost, formatAvailability, parseSkillList } from "./submissionFormatters";
import { copyTextToClipboard } from "../../../utils/clipboard";

// Label style matching the Corporate Azure detail-grid pattern.
const FIELD_LABEL_SX = {
  fontSize: "11px",
  fontWeight: 800,
  color: "portal.muted",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "0 !important",
};

const MissionDetailModal = ({ isOpen, onClose, missionId, onRefresh }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, features } = useBusinessUnit();
  const hasPilot = Boolean(features?.has_pilot);

  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [creatingConsultantId, setCreatingConsultantId] = useState(null);
  const [pools, setPools] = useState([]);
  const [addingToPoolAppId, setAddingToPoolAppId] = useState(null);
  const [selectedPoolId, setSelectedPoolId] = useState({});
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [downloadingCvId, setDownloadingCvId] = useState(null);
  const [deliveryStats, setDeliveryStats] = useState(null);

  const editModal = useDisclosure();

  const fetchMission = useCallback(async () => {
    if (!missionId) return;
    setLoading(true);
    try {
      const res = await getMission(missionId, {
        buId: selectedBusinessUnitId || undefined,
      });
      setMission(res);
    } catch {
      toast({
        title: t("partners.missions.detail.loadError", "Failed to load mission"),
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [missionId, selectedBusinessUnitId, t, toast]);

  const fetchApplications = useCallback(async () => {
    if (!missionId) return;
    setLoadingApps(true);
    try {
      const res = await getMissionApplications(missionId, { pageSize: 50 }, {
        buId: selectedBusinessUnitId || undefined,
      });
      setApplications(res.items || []);
    } catch {
      setApplications([]);
    } finally {
      setLoadingApps(false);
    }
  }, [missionId, selectedBusinessUnitId]);

  const fetchSubmissions = useCallback(async () => {
    if (!missionId) return;
    setLoadingSubs(true);
    try {
      const res = await getMissionSubmissions(missionId, { pageSize: 50 }, {
        buId: selectedBusinessUnitId || undefined,
      });
      setSubmissions(res.items || []);
    } catch {
      setSubmissions([]);
    } finally {
      setLoadingSubs(false);
    }
  }, [missionId, selectedBusinessUnitId]);

  const fetchPools = useCallback(async () => {
    try {
      const res = await listPools({ pageSize: 100 }, { buId: selectedBusinessUnitId || undefined });
      setPools(res.items || []);
    } catch {
      setPools([]);
    }
  }, [selectedBusinessUnitId]);

  const fetchDeliveryStats = useCallback(async () => {
    if (!missionId) return;
    try {
      const res = await getMissionDeliveryStats(missionId, {
        buId: selectedBusinessUnitId || undefined,
      });
      setDeliveryStats(res);
    } catch {
      setDeliveryStats(null);
    }
  }, [missionId, selectedBusinessUnitId]);

  useEffect(() => {
    if (isOpen && missionId) {
      fetchMission();
      fetchApplications();
      fetchSubmissions();
      fetchPools();
      fetchDeliveryStats();
    }
  }, [isOpen, missionId, fetchMission, fetchApplications, fetchSubmissions, fetchPools, fetchDeliveryStats]);

  const handleSend = async () => {
    if (!mission) return;
    setIsSending(true);
    try {
      await sendMission(mission.id, {
        buId: selectedBusinessUnitId || undefined,
      });
      toast({
        title: t("partners.missions.toasts.sent", "Mission sent successfully"),
        status: "success",
      });
      fetchMission();
      fetchDeliveryStats();
      onRefresh?.();
    } catch (e) {
      toast({
        title: t("partners.missions.toasts.sendError", "Failed to send mission"),
        description: e?.response?.data?.detail || "",
        status: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = async () => {
    if (!mission) return;
    setIsClosing(true);
    try {
      await closeMission(mission.id, {
        buId: selectedBusinessUnitId || undefined,
      });
      toast({
        title: t("partners.missions.toasts.closed", "Mission closed"),
        status: "success",
      });
      fetchMission();
      onRefresh?.();
    } catch (e) {
      toast({
        title: t("partners.missions.toasts.closeError", "Failed to close mission"),
        description: e?.response?.data?.detail || "",
        status: "error",
      });
    } finally {
      setIsClosing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!mission) return;
    try {
      const res = await getMissionLink(mission.id, {
        buId: selectedBusinessUnitId || undefined,
      });
      await copyTextToClipboard(res.url);
      toast({
        title: t("partners.missions.toasts.linkCopied", "Landing page link copied to clipboard"),
        status: "info",
      });
    } catch {
      toast({
        title: t("partners.missions.toasts.linkError", "Failed to copy link"),
        status: "error",
      });
    }
  };

  const buildLinkedInPost = useCallback(() => {
    if (!mission) return "";
    const notSpecified = t("partners.missions.linkedIn.notSpecified");
    const titleText = mission.title || notSpecified;
    const location = mission.location || notSpecified;
    const startDate = mission.startDate
      ? new Date(mission.startDate).toLocaleDateString()
      : "ASAP";
    let duration = notSpecified;
    if (mission.durationUnit === "months" && mission.durationMonths) {
      duration = t("partners.missions.linkedIn.durationMonths", { count: mission.durationMonths });
    } else if (mission.durationDays) {
      duration = t("partners.missions.linkedIn.durationDays", { count: mission.durationDays });
    }
    if (duration !== notSpecified && mission.isRenewable) {
      duration += ` ${t("partners.missions.linkedIn.renewable")}`;
    }
    const contractTypeMap = {
      freelance: t("partners.missions.form.contractTypeFreelance", "Freelance"),
      cdi: t("partners.missions.form.contractTypeCdi", "CDI"),
      portage: t("partners.missions.form.contractTypePortage", "Portage salarial"),
      sous_traitance: t("partners.missions.form.contractTypeSousTraitance", "Sous-traitance"),
    };
    const contractTypeList =
      Array.isArray(mission.contractTypes) && mission.contractTypes.length > 0
        ? mission.contractTypes
        : mission.contractType
        ? [mission.contractType]
        : [];
    const contractType = contractTypeList.length
      ? contractTypeList.map((c) => contractTypeMap[c] || c).join(", ")
      : notSpecified;

    const technical = parseSkillList(mission.skillsTechnical);
    const functional = parseSkillList(mission.skillsFunctional);
    const required = parseSkillList(mission.requiredSkills);
    const skills = [...technical, ...functional, ...required];
    const skillsLine = skills.length > 0
      ? t("partners.missions.linkedIn.skillsLine", { skills: skills.slice(0, 5).join(", ") })
      : t("partners.missions.linkedIn.skillsFallback");

    const applyUrl = mission.landingPageUrl || "www.asap-hub.ai";

    return [
      t("partners.missions.linkedIn.headline"),
      t("partners.missions.linkedIn.intro", { title: titleText }),
      "",
      t("partners.missions.linkedIn.infoSection"),
      t("partners.missions.linkedIn.locationLine", { value: location }),
      t("partners.missions.linkedIn.startDateLine", { value: startDate }),
      t("partners.missions.linkedIn.durationLine", { value: duration }),
      t("partners.missions.linkedIn.contractLine", { value: contractType }),
      "",
      t("partners.missions.linkedIn.profilesSection"),
      t("partners.missions.linkedIn.experienceLine", { title: titleText }),
      skillsLine,
      t("partners.missions.linkedIn.relationalLine"),
      "",
      t("partners.missions.linkedIn.applySection"),
      t("partners.missions.linkedIn.applyDirect"),
      "",
      applyUrl,
      "",
      t("partners.missions.linkedIn.closingLine1"),
      t("partners.missions.linkedIn.closingLine2"),
      t("partners.missions.linkedIn.closingLine3"),
      t("partners.missions.linkedIn.hashtags"),
    ].join("\n");
  }, [mission, t]);

  const handleCopyLinkedInPost = async () => {
    if (!mission) return;
    try {
      const post = buildLinkedInPost();
      await copyTextToClipboard(post);
      toast({
        title: t("partners.missions.toasts.linkedInCopied", "LinkedIn post copied to clipboard"),
        status: "success",
        duration: 3000,
      });
    } catch {
      toast({
        title: t("partners.missions.toasts.linkedInCopyError", "Failed to copy LinkedIn post"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleCreateConsultant = async (applicationId) => {
    setCreatingConsultantId(applicationId);
    try {
      const res = await createConsultantFromApplication(applicationId, {
        buId: selectedBusinessUnitId || undefined,
      });
      toast({
        title: t(
          "partners.missions.detail.consultantCreated",
          "Consultant created successfully"
        ),
        description: t(
          "partners.missions.detail.consultantCreatedDesc",
          "Consultant #{{id}} has been added to PILOT.",
          { id: res.consultantId }
        ),
        status: "success",
      });
      fetchApplications();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast({
        title: t(
          "partners.missions.detail.consultantError",
          "Failed to create consultant"
        ),
        description:
          detail === "APPLICATION_ALREADY_HAS_CONSULTANT"
            ? t(
                "partners.missions.detail.alreadyConsultant",
                "This application already has an associated consultant."
              )
            : detail,
        status: "error",
      });
    } finally {
      setCreatingConsultantId(null);
    }
  };

  const handleAddToPool = async (applicationId) => {
    const poolId = selectedPoolId[applicationId];
    if (!poolId) {
      toast({ title: t("partners.missions.detail.selectPool", "Please select a pool first"), status: "warning", duration: 3000 });
      return;
    }
    setAddingToPoolAppId(applicationId);
    try {
      await addToPoolFromApplication(poolId, applicationId, { buId: selectedBusinessUnitId || undefined });
      toast({
        title: t("partners.missions.detail.addedToPool", "Candidate added to pool"),
        status: "success",
        duration: 3000,
      });
      fetchApplications();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast({
        title: detail === "MEMBER_ALREADY_IN_POOL"
          ? t("partners.missions.detail.alreadyInPool", "This candidate is already in the selected pool")
          : t("partners.missions.detail.addToPoolError", "Failed to add to pool"),
        status: "error",
        duration: 4000,
      });
    } finally {
      setAddingToPoolAppId(null);
    }
  };

  const handleDownloadCv = async (type, id) => {
    setDownloadingCvId(`${type}-${id}`);
    try {
      const response = type === "application"
        ? await downloadApplicationCv(id, { buId: selectedBusinessUnitId || undefined })
        : await downloadSubmissionCv(id, { buId: selectedBusinessUnitId || undefined });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `cv-${id}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: t("partners.missions.detail.downloadError", "Failed to download CV"), status: "error", duration: 3000 });
    } finally {
      setDownloadingCvId(null);
    }
  };

  // Column definitions for the three DataTables.
  const recipientColumns = [
    { key: "type", label: t("partners.missions.detail.recipientType", "Type"), width: "20%" },
    { key: "name", label: t("partners.missions.detail.recipientName", "Name"), width: "50%" },
    { key: "emailSentAt", label: t("partners.missions.detail.emailSentAt", "Email Sent"), width: "30%" },
  ];

  const applicationColumns = [
    { key: "applicant", label: t("partners.missions.detail.applicant", "Applicant"), width: "35%" },
    { key: "score", label: t("partners.missions.detail.score", "Score"), width: "15%" },
    { key: "status", label: t("partners.missions.detail.appStatus", "Status"), width: "25%" },
    { key: "date", label: t("partners.missions.detail.appDate", "Date"), width: "25%" },
  ];

  const submissionColumns = [
    { key: "candidate", label: t("partners.missions.detail.candidate", "Candidate"), width: "25%" },
    { key: "submitter", label: t("partners.missions.detail.submitter", "Submitted By"), width: "15%" },
    { key: "score", label: t("partners.incoming.score", "Score"), width: "10%" },
    { key: "cost", label: t("partners.missions.detail.cost", "Cost"), width: "12%" },
    { key: "availability", label: t("partners.missions.detail.availability", "Availability"), width: "13%" },
    { key: "status", label: t("common.status", "Status"), width: "13%" },
    { key: "date", label: t("common.createdAt", "Date"), width: "12%" },
  ];

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {loading ? (
              t("partners.missions.detail.loading", "Loading...")
            ) : (
              <Flex align="center" gap={3}>
                <Text sx={{ marginBottom: "0 !important" }} lineHeight="normal">
                  {mission?.title || ""}
                </Text>
                {mission && (
                  <StatusBadge
                    label={getStatusLabel(
                      t,
                      PARTNERS_MISSION_STATUS_LABEL_KEYS,
                      PARTNERS_MISSION_STATUS_LABELS,
                      mission.status
                    )}
                    colorScheme={PARTNERS_MISSION_STATUS_COLORS[mission.status] || "gray"}
                  />
                )}
              </Flex>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loading ? (
              <Flex justify="center" py={8}>
                <Spinner />
              </Flex>
            ) : !mission ? (
              <Text color="gray.500">
                {t("partners.missions.detail.notFound", "Mission not found")}
              </Text>
            ) : (
              <Tabs variant="line" colorScheme="brand" isFitted>
                <TabList>
                  <Tab>{t("partners.missions.detail.tabs.info", "Info")}</Tab>
                  <Tab>
                    {t("partners.missions.detail.tabs.recipients", "Recipients")} (
                    {mission.recipients?.length || 0})
                  </Tab>
                  <Tab>
                    {t("partners.missions.detail.tabs.applications", "Applications")} (
                    {applications.length})
                  </Tab>
                  <Tab>
                    {t("partners.missions.detail.tabs.submissions", "Partner Submissions")} (
                    {submissions.length})
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Info Tab */}
                  <TabPanel px={0} pt={4}>
                    <VStack spacing={4} align="stretch">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <InfoField
                          label={t("partners.missions.detail.purpose", "Purpose")}
                          value={
                            mission.purpose === "contact_pool"
                              ? t("partners.missions.purposeLabels.contactPool", "Contact Pool")
                              : mission.purpose === "propose_assignment"
                              ? t("partners.missions.purposeLabels.proposeAssignment", "Assignment")
                              : mission.purpose || "-"
                          }
                        />
                        <InfoField
                          label={t("partners.missions.detail.location", "Location")}
                          value={mission.location || "-"}
                        />
                        <InfoField
                          label={t("partners.missions.detail.tjm", "TJM")}
                          value={
                            mission.tjmMin || mission.tjmMax
                              ? `${mission.tjmMin || "?"}€ - ${mission.tjmMax || "?"}€`
                              : "-"
                          }
                        />
                        <InfoField
                          label={t("partners.missions.detail.startDate", "Start Date")}
                          value={mission.startDate || "-"}
                        />
                        <InfoField
                          label={t("partners.missions.detail.duration", "Duration")}
                          value={(() => {
                            let durationStr = "-";
                            if (mission.durationUnit === "months" && mission.durationMonths) {
                              durationStr = `${mission.durationMonths} ${t("partners.missions.form.months", "mois")}`;
                            } else if (mission.durationDays) {
                              durationStr = `${mission.durationDays} ${t("partners.missions.form.days", "jours")}`;
                            }
                            if (durationStr !== "-" && mission.isRenewable) {
                              durationStr += ` ${t("partners.missions.form.renewable", "(renouvelable)")}`;
                            }
                            return durationStr;
                          })()}
                        />
                        <InfoField
                          label={t("partners.missions.detail.contractType", "Contract Type")}
                          value={(() => {
                            const list =
                              Array.isArray(mission.contractTypes) && mission.contractTypes.length > 0
                                ? mission.contractTypes
                                : mission.contractType
                                ? [mission.contractType]
                                : [];
                            if (!list.length) return "-";
                            const map = {
                              freelance: t("partners.missions.form.contractTypeFreelance", "Freelance"),
                              cdi: t("partners.missions.form.contractTypeCdi", "CDI"),
                              portage: t("partners.missions.form.contractTypePortage", "Portage salarial"),
                              sous_traitance: t("partners.missions.form.contractTypeSousTraitance", "Sous-traitance"),
                            };
                            return list.map((ct) => map[ct] || ct).join(", ");
                          })()}
                        />
                        <InfoField
                          label={t("partners.missions.detail.atsThreshold", "ATS Threshold")}
                          value={mission.atsThreshold === "none" ? "-" : `${mission.atsThreshold}%`}
                        />
                        <InfoField
                          label={t("partners.missions.detail.skillsEvaluation", "SKILLS Evaluation")}
                          value={(() => {
                            try {
                              const cfg = JSON.parse(mission.evaluationConfig || "{}");
                              if (cfg.enabled) {
                                const count = (cfg.job_matrix || []).length;
                                return `${cfg.language || "?"} · ${count} skill${count !== 1 ? "s" : ""}`;
                              }
                            } catch { /* ignore */ }
                            return mission.evaluationQuestionnaireId
                              ? t("partners.missions.detail.evaluationAttached", "Evaluation attached")
                              : "-";
                          })()}
                        />
                        <InfoField
                          label={t("partners.missions.detail.created", "Created")}
                          value={
                            mission.createdAt
                              ? new Date(mission.createdAt).toLocaleString()
                              : "-"
                          }
                        />
                      </SimpleGrid>

                      {mission.description && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.description", "Description")}
                          </Text>
                          <Text fontSize="sm" whiteSpace="pre-wrap">
                            {mission.description}
                          </Text>
                        </Box>
                      )}

                      {parseSkillList(mission.skillsTechnical).length > 0 && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.technicalSkills", "Technical Skills")}
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {parseSkillList(mission.skillsTechnical).map((s, i) => (
                              <Tag key={i} colorScheme="purple" size="sm" variant="solid">{s}</Tag>
                            ))}
                          </Flex>
                        </Box>
                      )}

                      {parseSkillList(mission.skillsFunctional).length > 0 && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.functionalSkills", "Functional Skills")}
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {parseSkillList(mission.skillsFunctional).map((s, i) => (
                              <Tag key={i} colorScheme="orange" size="sm" variant="solid">{s}</Tag>
                            ))}
                          </Flex>
                        </Box>
                      )}

                      {parseSkillList(mission.skillsBusiness).length > 0 && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.businessSkills", "Business Skills")}
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {parseSkillList(mission.skillsBusiness).map((s, i) => (
                              <Tag key={i} colorScheme="green" size="sm" variant="solid">{s}</Tag>
                            ))}
                          </Flex>
                        </Box>
                      )}

                      {parseSkillList(mission.requiredSkills).length > 0 && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.requiredSkills", "Required Skills")}
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {parseSkillList(mission.requiredSkills).map((s, i) => (
                              <Tag key={i} colorScheme="teal" size="sm">{s}</Tag>
                            ))}
                          </Flex>
                        </Box>
                      )}

                      {parseSkillList(mission.optionalSkills).length > 0 && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.optionalSkills", "Optional Skills")}
                          </Text>
                          <Flex gap={2} flexWrap="wrap">
                            {parseSkillList(mission.optionalSkills).map((s, i) => (
                              <Tag key={i} colorScheme="blue" size="sm">{s}</Tag>
                            ))}
                          </Flex>
                        </Box>
                      )}

                      {mission.landingPageUrl && (
                        <Box>
                          <Text sx={FIELD_LABEL_SX} mb={1}>
                            {t("partners.missions.detail.landingPage", "Landing Page")}
                          </Text>
                          <Flex align="center" gap={2}>
                            <Text fontSize="sm" color="teal.600" isTruncated>
                              {mission.landingPageUrl}
                            </Text>
                            <MiniIconButton
                              icon={<FaCopy />}
                              aria-label={t("partners.missions.detail.copyLinkAria", "Copy link")}
                              onClick={handleCopyLink}
                            />
                          </Flex>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Recipients Tab */}
                  <TabPanel px={0} pt={4}>
                    {deliveryStats && deliveryStats.total > 0 && (
                      <Box
                        mb={4}
                        p={4}
                        borderWidth="1px"
                        borderColor="portal.border"
                        borderRadius="8px"
                      >
                        <Text
                          fontSize="11px"
                          fontWeight="800"
                          textTransform="uppercase"
                          letterSpacing="0.055em"
                          color="portal.muted"
                          mb={3}
                          sx={{ marginBottom: "12px !important" }}
                        >
                          {t("partners.missions.detail.deliverability.title", "Email deliverability")}
                        </Text>
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                          {[
                            { label: t("partners.missions.detail.deliverability.sent", "Sent"), value: deliveryStats.sent },
                            { label: t("partners.missions.detail.deliverability.delivered", "Delivered"), value: deliveryStats.delivered },
                            { label: t("partners.missions.detail.deliverability.bounced", "Bounced"), value: deliveryStats.bounced },
                            { label: t("partners.missions.detail.deliverability.complained", "Complaints"), value: deliveryStats.complained },
                          ].map((m) => (
                            <Box key={m.label}>
                              <Text fontSize="22px" fontWeight="800" lineHeight="1" sx={{ marginBottom: "2px !important" }}>
                                {m.value}
                              </Text>
                              <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {m.label}
                              </Text>
                            </Box>
                          ))}
                        </SimpleGrid>
                        <Flex mt={3} gap={6} flexWrap="wrap">
                          {[
                            { label: t("partners.missions.detail.deliverability.deliveryRate", "Delivery rate"), rate: deliveryStats.delivery_rate, scheme: "green" },
                            { label: t("partners.missions.detail.deliverability.bounceRate", "Bounce rate"), rate: deliveryStats.bounce_rate, scheme: "red" },
                            { label: t("partners.missions.detail.deliverability.complaintRate", "Complaint rate"), rate: deliveryStats.complaint_rate, scheme: "orange" },
                          ].map((m) => (
                            <Flex key={m.label} align="center" gap={2}>
                              <Badge colorScheme={m.scheme} variant="subtle">
                                {`${((m.rate || 0) * 100).toFixed(1)}%`}
                              </Badge>
                              <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {m.label}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>
                    )}
                    <DataTable
                      columns={recipientColumns}
                      rows={mission.recipients || []}
                      loading={false}
                      emptyState={
                        <EmptyState
                          title={t("partners.missions.detail.noRecipients", "No recipients added")}
                        />
                      }
                      renderRow={(r) => (
                        <Tr key={r.id}>
                          <Td>
                            <Badge
                              colorScheme={r.recipientType === "pool" ? "teal" : "purple"}
                              variant="subtle"
                            >
                              {r.recipientType === "pool"
                                ? t("partners.missions.detail.recipientTypePool", "Pool")
                                : t("partners.missions.detail.recipientTypePartner", "Partner")}
                            </Badge>
                          </Td>
                          <Td>{r.poolName || r.partnerCompanyName || "-"}</Td>
                          <Td>
                            {r.emailSentAt
                              ? new Date(r.emailSentAt).toLocaleString()
                              : "-"}
                          </Td>
                        </Tr>
                      )}
                    />
                  </TabPanel>

                  {/* Applications Tab — click row for detail */}
                  <TabPanel px={0} pt={4}>
                    <DataTable
                      columns={applicationColumns}
                      rows={applications}
                      loading={loadingApps}
                      emptyState={
                        <EmptyState
                          title={t("partners.missions.detail.noApplications", "No applications received yet")}
                        />
                      }
                      renderRow={(app) => (
                        <Tr
                          key={app.id}
                          cursor="pointer"
                          onClick={() => setSelectedApp(app)}
                        >
                          <Td>
                            <Flex align="center" gap={1} sx={{ "& p": { marginBottom: "0 !important" } }}>
                              <Text sx={{ marginBottom: "0 !important" }}>
                                {app.firstName} {app.lastName}
                              </Text>
                              {app.isReferral && (
                                <Badge colorScheme="orange" variant="subtle" fontSize="2xs">
                                  {t("partners.missions.detail.referral", "Referral")}
                                </Badge>
                              )}
                              {app.cvUrl && (
                                <Badge colorScheme="blue" variant="subtle" fontSize="2xs">CV</Badge>
                              )}
                            </Flex>
                          </Td>
                          <Td>
                            {app.score != null ? (
                              <Badge colorScheme={app.score >= 80 ? "green" : app.score >= 50 ? "yellow" : "red"}>
                                {app.score}%
                              </Badge>
                            ) : "-"}
                          </Td>
                          <Td>
                            <StatusBadge
                              label={getStatusLabel(t, null, PARTNERS_SUBMISSION_STATUS_LABELS, app.status) || app.status}
                              colorScheme={PARTNERS_SUBMISSION_STATUS_COLORS[app.status] || "gray"}
                            />
                          </Td>
                          <Td>{app.createdAt ? new Date(app.createdAt).toLocaleDateString() : "-"}</Td>
                        </Tr>
                      )}
                    />
                  </TabPanel>

                  {/* Application Detail Modal — nested */}
                  {selectedApp && (
                    <Modal isOpen={!!selectedApp} onClose={() => setSelectedApp(null)} size="lg">
                      <ModalOverlay bg="rgba(11,31,58,.45)" />
                      <ModalContent sx={MODAL_CONTENT_SX}>
                        <ModalHeader sx={MODAL_HEADER_SX}>
                          <Text sx={{ marginBottom: "0 !important" }}>
                            {selectedApp.firstName} {selectedApp.lastName}
                          </Text>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          <VStack align="stretch" spacing={3}>
                            <SimpleGrid columns={2} spacing={3}>
                              <Box>
                                <Text sx={FIELD_LABEL_SX}>{t("common.email", "Email")}</Text>
                                <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>{selectedApp.email}</Text>
                              </Box>
                              <Box>
                                <Text sx={FIELD_LABEL_SX}>{t("partners.missions.detail.phone", "Phone")}</Text>
                                <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>{selectedApp.phone || "-"}</Text>
                              </Box>
                              <Box>
                                <Text sx={FIELD_LABEL_SX}>{t("partners.missions.detail.score", "Score")}</Text>
                                {selectedApp.score != null ? (
                                  <Badge colorScheme={selectedApp.score >= 80 ? "green" : selectedApp.score >= 50 ? "yellow" : "red"}>
                                    {selectedApp.score}%
                                  </Badge>
                                ) : <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>-</Text>}
                              </Box>
                              <Box>
                                <Text sx={FIELD_LABEL_SX}>{t("common.status", "Status")}</Text>
                                <StatusBadge
                                  label={getStatusLabel(t, null, PARTNERS_SUBMISSION_STATUS_LABELS, selectedApp.status) || selectedApp.status}
                                  colorScheme={PARTNERS_SUBMISSION_STATUS_COLORS[selectedApp.status] || "gray"}
                                />
                              </Box>
                              <Box>
                                <Text sx={FIELD_LABEL_SX}>{t("common.createdAt", "Date")}</Text>
                                <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>
                                  {selectedApp.createdAt ? new Date(selectedApp.createdAt).toLocaleDateString() : "-"}
                                </Text>
                              </Box>
                              {selectedApp.isReferral && (
                                <Box>
                                  <Text sx={FIELD_LABEL_SX}>{t("partners.missions.detail.referredBy", "Referred by")}</Text>
                                  <Text fontSize="sm" sx={{ marginBottom: "0 !important" }}>
                                    {selectedApp.referrerFirstName || ""} {selectedApp.referrerLastName || ""}
                                  </Text>
                                </Box>
                              )}
                            </SimpleGrid>

                            {/* Applicant Skills — categorized */}
                            {selectedApp.applicantSkillsTechnical?.length > 0 && (
                              <Box>
                                <Text sx={FIELD_LABEL_SX} mb={1}>
                                  {t("partners.missions.detail.technicalSkills", "Technical Skills")}
                                </Text>
                                <Flex wrap="wrap" gap={1}>
                                  {selectedApp.applicantSkillsTechnical.map((skill, idx) => (
                                    <Badge key={idx} colorScheme="purple" variant="subtle" fontSize="xs" px={2} py={0.5}>
                                      {skill}
                                    </Badge>
                                  ))}
                                </Flex>
                              </Box>
                            )}
                            {selectedApp.applicantSkillsFunctional?.length > 0 && (
                              <Box>
                                <Text sx={FIELD_LABEL_SX} mb={1}>
                                  {t("partners.missions.detail.functionalSkills", "Functional Skills")}
                                </Text>
                                <Flex wrap="wrap" gap={1}>
                                  {selectedApp.applicantSkillsFunctional.map((skill, idx) => (
                                    <Badge key={idx} colorScheme="orange" variant="subtle" fontSize="xs" px={2} py={0.5}>
                                      {skill}
                                    </Badge>
                                  ))}
                                </Flex>
                              </Box>
                            )}
                            {selectedApp.applicantSkillsBusiness?.length > 0 && (
                              <Box>
                                <Text sx={FIELD_LABEL_SX} mb={1}>
                                  {t("partners.missions.detail.businessSkills", "Business Skills")}
                                </Text>
                                <Flex wrap="wrap" gap={1}>
                                  {selectedApp.applicantSkillsBusiness.map((skill, idx) => (
                                    <Badge key={idx} colorScheme="green" variant="subtle" fontSize="xs" px={2} py={0.5}>
                                      {skill}
                                    </Badge>
                                  ))}
                                </Flex>
                              </Box>
                            )}
                            {/* Fallback: flat skills when no categories available */}
                            {!selectedApp.applicantSkillsTechnical?.length &&
                              !selectedApp.applicantSkillsFunctional?.length &&
                              !selectedApp.applicantSkillsBusiness?.length &&
                              selectedApp.applicantSkills?.length > 0 && (
                              <Box>
                                <Text sx={FIELD_LABEL_SX} mb={1}>
                                  {t("partners.missions.detail.skills", "Skills")}
                                </Text>
                                <Flex wrap="wrap" gap={1}>
                                  {selectedApp.applicantSkills.map((skill, idx) => (
                                    <Badge key={idx} colorScheme="blue" variant="subtle" fontSize="xs">
                                      {skill}
                                    </Badge>
                                  ))}
                                </Flex>
                              </Box>
                            )}

                            {/* CV Download */}
                            {selectedApp.cvUrl && (
                              <Button
                                size="sm"
                                leftIcon={<FaDownload />}
                                variant="secondary"
                                onClick={() => handleDownloadCv("application", selectedApp.id)}
                                isLoading={downloadingCvId === `application-${selectedApp.id}`}
                                loadingText={t("partners.missions.detail.downloadingCv", "Downloading...")}
                              >
                                {t("partners.missions.detail.downloadCv", "Download CV")}
                              </Button>
                            )}

                            {/* Pool */}
                            <Box>
                              <Text sx={FIELD_LABEL_SX} mb={1}>
                                {t("partners.missions.detail.pool", "Pool")}
                              </Text>
                              {selectedApp.isNewCandidate ? (
                                <Flex gap={2} align="center" sx={{ "& select, & button": { marginBottom: "0 !important" } }}>
                                  <Select
                                    size="sm"
                                    placeholder={t("partners.missions.detail.selectPool", "Select pool")}
                                    value={selectedPoolId[selectedApp.id] || ""}
                                    onChange={(e) => setSelectedPoolId((prev) => ({ ...prev, [selectedApp.id]: e.target.value }))}
                                    w="200px"
                                    sx={{ paddingTop: "0 !important", paddingBottom: "0 !important", lineHeight: "32px !important", height: "32px !important" }}
                                  >
                                    {pools.map((p) => (
                                      <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                  </Select>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleAddToPool(selectedApp.id)}
                                    isLoading={addingToPoolAppId === selectedApp.id}
                                  >
                                    {t("partners.missions.detail.addToPool", "Add to pool")}
                                  </Button>
                                </Flex>
                              ) : (
                                <Badge colorScheme="gray" variant="subtle">
                                  {t("partners.missions.detail.inPool", "Already in pool")}
                                </Badge>
                              )}
                            </Box>

                            {/* PILOT actions */}
                            {hasPilot && (
                              <Box>
                                <Text sx={FIELD_LABEL_SX} mb={1}>PILOT</Text>
                                {selectedApp.consultantId ? (
                                  <Badge colorScheme="green" variant="subtle">
                                    {t("partners.missions.detail.consultantLinked", "Consultant #{{id}}", { id: selectedApp.consultantId })}
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleCreateConsultant(selectedApp.id)}
                                    isLoading={creatingConsultantId === selectedApp.id}
                                  >
                                    {t("partners.missions.detail.createConsultant", "Create Consultant")}
                                  </Button>
                                )}
                              </Box>
                            )}
                          </VStack>
                        </ModalBody>
                        <ModalFooter sx={MODAL_FOOTER_SX}>
                          <Button variant="secondary" size="sm" onClick={() => setSelectedApp(null)}>
                            {t("partners.missions.detail.close", "Close")}
                          </Button>
                        </ModalFooter>
                      </ModalContent>
                    </Modal>
                  )}

                  {/* Partner Submissions Tab — click row for detail */}
                  <TabPanel px={0} pt={4}>
                    <DataTable
                      columns={submissionColumns}
                      rows={submissions}
                      loading={loadingSubs}
                      emptyState={
                        <EmptyState
                          title={t("partners.missions.detail.noSubmissions", "No partner submissions received yet")}
                        />
                      }
                      renderRow={(sub) => (
                        <Tr
                          key={sub.id}
                          cursor="pointer"
                          onClick={() => setSelectedSub(sub)}
                        >
                          <Td>
                            <Flex align="center" gap={1} flexWrap="wrap">
                              <Text sx={{ marginBottom: "0 !important" }}>
                                {sub.title || t("partners.missions.detail.submissionFallback", "Submission #{{id}}", { id: sub.id })}
                              </Text>
                              {sub.location && (
                                <Text fontSize="xs" color="gray.500" sx={{ marginBottom: "0 !important" }}>
                                  · {sub.location}
                                </Text>
                              )}
                              {(sub.sourceCvUrl || sub.cvUrl) && (
                                <Badge colorScheme="blue" variant="subtle" fontSize="2xs">CV</Badge>
                              )}
                            </Flex>
                          </Td>
                          <Td>{sub.submitterCompanyName || "-"}</Td>
                          <Td>
                            {sub.score != null ? (
                              <Badge colorScheme={sub.score >= 80 ? "green" : sub.score >= 50 ? "yellow" : "red"}>
                                {sub.score}%
                              </Badge>
                            ) : "-"}
                          </Td>
                          <Td>{formatCost(sub.costAmount, sub.costType)}</Td>
                          <Td>{formatAvailability(sub.availabilityDate, sub.availabilityStatus)}</Td>
                          <Td>
                            <StatusBadge
                              label={getStatusLabel(t, null, PARTNERS_SUBMISSION_STATUS_LABELS, sub.status) || sub.status}
                              colorScheme={PARTNERS_SUBMISSION_STATUS_COLORS[sub.status] || "gray"}
                            />
                          </Td>
                          <Td>{sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : "-"}</Td>
                        </Tr>
                      )}
                    />
                  </TabPanel>

                  <SubmissionDetailModal
                    submission={selectedSub}
                    onClose={() => setSelectedSub(null)}
                    showSubmitter
                  />
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Flex w="100%" justify="space-between">
              <Button variant="secondary" size="sm" onClick={onClose}>
                {t("partners.missions.detail.close", "Close")}
              </Button>
              {mission && (
                <Flex gap={2}>
                  <Button size="sm" variant="secondary" onClick={handleCopyLink}>
                    <FaCopy style={{ marginRight: 6 }} />
                    {t("partners.missions.detail.copyLink", "Copy Link")}
                  </Button>
                  {mission.status !== "draft" && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="linkedin"
                      onClick={handleCopyLinkedInPost}
                      data-testid="copy-linkedin-post"
                      sx={{
                        color: "#0077B5 !important",
                        borderColor: "#0077B5 !important",
                      }}
                      _hover={{
                        bg: "#E8F4F9",
                        color: "#0077B5 !important",
                      }}
                    >
                      <FaLinkedin style={{ marginRight: 6 }} />
                      {t("partners.missions.detail.copyLinkedIn", "Copy LinkedIn Post")}
                    </Button>
                  )}
                  {mission.status === "draft" && (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => editModal.onOpen()}
                      >
                        {t("partners.missions.detail.edit", "Edit")}
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={handleSend}
                        isLoading={isSending}
                      >
                        {t("partners.missions.detail.send", "Send")}
                      </Button>
                    </>
                  )}
                  {mission.status !== "closed" &&
                    mission.status !== "expired" &&
                    mission.status !== "draft" && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={handleClose}
                        isLoading={isClosing}
                      >
                        {t("partners.missions.detail.closeMission", "Close Mission")}
                      </Button>
                    )}
                </Flex>
              )}
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {mission && (
        <MissionFormModal
          isOpen={editModal.isOpen}
          onClose={editModal.onClose}
          mission={mission}
          onSuccess={() => {
            fetchMission();
            onRefresh?.();
          }}
        />
      )}
    </>
  );
};

const InfoField = ({ label, value }) => (
  <Box>
    <Text
      sx={FIELD_LABEL_SX}
      mb={1}
    >
      {label}
    </Text>
    <Text fontSize="sm" color="portal.text" sx={{ marginBottom: "0 !important" }}>
      {value}
    </Text>
  </Box>
);

export default MissionDetailModal;
